import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalCloseClickLocation } from '.';
import Button, { ButtonType } from '../Button';
import { CloseButton } from '../Button/CloseButton';
import { IconSize } from '../Icon';
import type { ModalProps } from './types';

type HeaderTextStyle =
  | 'text-h1'
  | 'text-h2'
  | 'text-h3'
  | 'text-h4'
  | 'text-h4-bold'
  | 'text-h5'
  | 'text-h5-bold'
  | 'text-h6'
  | 'text-h6-bold';

interface StyledModalProps extends React.PropsWithChildren<ModalProps> {
  headerText?: React.ReactNode;
  headerTextStyle?: HeaderTextStyle;
  subText?: string;
  showCloseButton?: boolean;
  closeButtonIconSize?: IconSize;
  primaryButtonText?: string;
  primaryDisabled?: boolean;
  primaryButtonClassName?: string;
  secondaryButtonText?: string;
  onAccept?: () => void;
  onClickSecondaryButton?: () => void;
  removeContainerPadding?: boolean;
  contentClassName?: string;
  hideOverflowGradient?: boolean;
  onOutsideClick?: (clickLocation?: ModalCloseClickLocation) => void;
}

export const StyledModal: React.FC<StyledModalProps> = ({
  children,
  modalType,
  headerText,
  headerTextStyle,
  subText,
  isShowing,
  closeButtonIconSize,
  primaryButtonText,
  primaryButtonClassName,
  primaryDisabled = false,
  secondaryButtonText,
  onClickSecondaryButton,
  showCloseButton = true,
  removeContainerPadding,
  contentClassName,
  hideOverflowGradient = false,
  preventExternalClose = false,
  ariaLabel,
  onOutsideClick,
  onClose,
  onAccept,
}) => {
  const [contentIsOverflowing, setContentIsOverflowing] = useState<boolean>(false);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleSetContentIsOverflowing = useCallback(([contentElementEntry]: ResizeObserverEntry[]) => {
    const wrapperHeight = contentWrapperRef.current?.getBoundingClientRect()?.height ?? 0;
    if (!contentElementEntry || !wrapperHeight) return;

    const contentHeight = contentElementEntry.contentRect.height;
    setContentIsOverflowing(contentHeight > wrapperHeight);
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleSetContentIsOverflowing);

    if (contentRef.current && contentWrapperRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [contentWrapperRef, contentRef, handleSetContentIsOverflowing]);

  const handleCloseClick = () => {
    onClose?.('close-button-click');
  };

  const handleModalClose = (clickLocation: ModalCloseClickLocation) => {
    onClose?.(clickLocation);
  };

  const modalHeader = (
    <div className='inset-x-0 top-0 flex w-full flex-col p-6 pb-4'>
      <div
        className={cn('flex w-full items-center', {
          'justify-center': headerText && !showCloseButton,
          'justify-end': !headerText && showCloseButton,
          'justify-between': headerText && showCloseButton,
          hidden: !headerText && !showCloseButton,
        })}>
        <div>
          {headerText && <p className={cn(headerTextStyle ?? 'text-2xl font-bold', 'text-eggplant')}>{headerText}</p>}
        </div>
        {showCloseButton && (
          <CloseButton className='self-start' iconSize={closeButtonIconSize} onClick={handleCloseClick} />
        )}
      </div>
      {subText && <p className='m-0 text-body-400 text-eggplant-400'>{subText}</p>}
    </div>
  );

  const gradiantOverlay = (
    <div
      className={cn(
        'pointer-events-none absolute -top-6 left-0 h-6 w-full',
        'bg-gradient-to-t from-neutral-100 to-transparent'
      )}
    />
  );

  const modalFooter = (
    <div
      className={cn('relative justify-self-end', {
        'px-6 py-4': secondaryButtonText || primaryButtonText,
        'flex w-full justify-between': secondaryButtonText && primaryButtonText,
      })}>
      {/* Gradiant overlay when content is scrollable */}
      {contentIsOverflowing && !hideOverflowGradient && gradiantOverlay}

      {secondaryButtonText && (
        <Button buttonType={ButtonType.TextLink} onClick={onClickSecondaryButton ?? handleCloseClick}>
          {secondaryButtonText}
        </Button>
      )}
      {primaryButtonText && onAccept && (
        <div className={primaryButtonClassName}>
          <Button
            renderFullWidth={!secondaryButtonText}
            buttonType={ButtonType.Primary}
            onClick={onAccept}
            disabled={primaryDisabled}>
            {primaryButtonText}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isShowing={isShowing}
      onClose={handleModalClose}
      modalType={modalType}
      onOutsideClick={onOutsideClick}
      preventExternalClose={preventExternalClose}
      ariaLabel={ariaLabel ?? (typeof headerText === 'string' ? headerText : undefined)}>
      <div className='relative flex h-full flex-col overflow-hidden'>
        {(headerText || subText || showCloseButton) && modalHeader}

        <div
          ref={contentWrapperRef}
          className={cn('relative w-full flex-1 overflow-y-auto overflow-x-hidden', contentClassName)}>
          <div ref={contentRef} className={cn({ 'px-6': !removeContainerPadding })}>
            {children}
          </div>
        </div>

        {modalFooter}
      </div>
    </Modal>
  );
};
